<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddLossReason from './AddLossReason.vue';
import EditLossReason from './EditLossReason.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedReason = ref({});

const records = computed(() => getters['lossReasons/getLossReasons'].value);
const uiFlags = computed(() => getters['lossReasons/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedReason.value.name}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedReason.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedReason.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLossReason = async id => {
  try {
    await store.dispatch('lossReasons/delete', id);
    useAlert(t('LOSS_REASON_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LOSS_REASON_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedReason.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedReason.value.id] = true;
  closeDeletePopup();
  deleteLossReason(selectedReason.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('LOSS_REASON_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LOSS_REASON_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LOSS_REASON_MGMT.LIST.TABLE_HEADER.STATUS'),
  ];
});

onBeforeMount(() => {
  store.dispatch('lossReasons/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LOSS_REASON_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LOSS_REASON_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('LOSS_REASON_MGMT.HEADER')"
        :description="$t('LOSS_REASON_MGMT.DESCRIPTION')"
        feature-name="loss_reasons"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('LOSS_REASON_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="reason in records" :key="reason.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ reason.name }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ reason.description || '-' }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="px-2 py-1 text-xs font-medium rounded-full"
                :class="
                  reason.is_active
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                "
              >
                {{
                  reason.is_active
                    ? $t('LOSS_REASON_MGMT.STATUS.ACTIVE')
                    : $t('LOSS_REASON_MGMT.STATUS.INACTIVE')
                }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('LOSS_REASON_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[reason.id]"
                  @click="openEditPopup(reason)"
                />
                <Button
                  v-tooltip.top="$t('LOSS_REASON_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[reason.id]"
                  @click="openDeletePopup(reason)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLossReason @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLossReason
        :selected-response="selectedReason"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LOSS_REASON_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LOSS_REASON_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LOSS_REASON_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LOSS_REASON_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
